import type { DashboardProject } from "../../types.js";
import type {
  DashboardProjectRecordDto,
  ProjectsSummary,
  ProjectsTabId,
  ProjectsWorkspaceSnapshotViewModel,
  DashboardWorkspaceSnapshotDto,
} from "../types/projects.api";

export function getProjectsDisplayStatus(
  status: DashboardProject["status"] | ProjectsWorkspaceSnapshotViewModel["backendState"],
): "active" | "staged" | "completed" {
  if (status === "delivered" || status === "approved") {
    return "completed";
  }

  if (status === "ACTIVE" || status === "in_production" || status === "awaiting_review" || status === "revision_requested") {
    return "active";
  }

  return "staged";
}

export function mapProjectRecordToViewModel(
  project: DashboardProjectRecordDto,
): DashboardProject {
  return {
    id: project.id,
    name: project.title,
    companyName: project.companyName,
    updatedAtLabel: project.updatedAtLabel,
    status: project.status,
    collaborators: project.collaborators,
  };
}

export function mapProjectRecordsToViewModels(
  projects: ReadonlyArray<DashboardProjectRecordDto>,
): DashboardProject[] {
  return projects.map(mapProjectRecordToViewModel);
}

export function buildProjectsSummary(
  projects: ReadonlyArray<DashboardProject>,
): ProjectsSummary {
  return projects.reduce<ProjectsSummary>(
    (summary, project) => {
      summary.total += 1;
      summary[getProjectsDisplayStatus(project.status)] += 1;
      return summary;
    },
    { total: 0, active: 0, completed: 0, staged: 0 },
  );
}

export function filterDashboardProjects(
  projects: ReadonlyArray<DashboardProject>,
  tab: ProjectsTabId,
  query: string,
): DashboardProject[] {
  const normalizedQuery = query.trim().toLowerCase();

  return projects.filter((project) => {
    const matchesTab =
      tab === "all" ? true : getProjectsDisplayStatus(project.status) === tab;
    const matchesQuery =
      normalizedQuery.length === 0 ||
      [project.name, project.companyName, project.updatedAtLabel, ...project.collaborators]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);

    return matchesTab && matchesQuery;
  });
}

function getWorkspaceStateLabel(
  backendState: DashboardWorkspaceSnapshotDto["backendState"],
  hasLiveDeployment: boolean,
): string {
  if (!backendState) {
    return "Workspace snapshot unavailable";
  }

  if (backendState === "ACTIVE") {
    return hasLiveDeployment ? "Live workspace" : "Published snapshot";
  }

  if (backendState === "UPDATED") {
    return hasLiveDeployment
      ? "Live workspace with draft updates"
      : "Workspace updated";
  }

  return "Workspace drafting";
}

export function mapWorkspaceSnapshotToViewModel(
  snapshot: DashboardWorkspaceSnapshotDto,
): ProjectsWorkspaceSnapshotViewModel {
  return {
    ...snapshot,
    stateLabel: getWorkspaceStateLabel(
      snapshot.backendState,
      snapshot.hasLiveDeployment,
    ),
  };
}
